import { FormationsAsList } from "@/api/privateRequests/squad-builder/get-formations";
import { getAuthors } from "@/api/publicRequests/tactics/getAuthors";
import {
  FORMATION_GROUPS_3_AT_THE_BACK,
  FORMATION_GROUPS_4_AT_THE_BACK,
  FORMATION_GROUPS_5_AT_THE_BACK,
} from "@/features/Tactics/Sidebars/TacticFiltersSidebar";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { APIStatus } from "../../api/APIStatus.type";
import * as apiFc24 from "../../api/apiFc24";
import { deleteTactic as _deleteTactic } from "../../api/publicRequests/tactics/deleteTactic";
import { editTactic as _editTactic } from "../../api/publicRequests/tactics/editTactic";
import { getTacticFromCode as _getTacticFromCode } from "../../api/publicRequests/tactics/getTacticFromCode";
import { uploadTactic as _uploadTactic } from "../../api/publicRequests/tactics/uploadTactic";
import { setCustomTactic } from "../squadBuilder/defaultSquadBuilder/defaultSquadBuilderSlice";
import { IRootState } from "../store";

export type Tactic = {
  id?: string;
  name: string;
  description: string;
  popularityScore: number;
  logoUrl: string;
  author: string;
  tags: string[];
  eaCode: string;
  isTrending: boolean;
  isFavorite: boolean;
  isNew: boolean;
  publishedAt: number;
  teamTactics: {
    buildUpStyle: string;
    formationId: string;
    defensiveApproach: string;
    lineHeight: number;
  };
  playerRoles: {
    playerRoleId: number;
    focus: string;
  }[];
  socials?: {
    twitchLink?: string;
    ytLink?: string;
    instagramLink?: string;
    fbLink?: string;
    xLink?: string;
  };
  canEdit?: boolean;
};

export type TacticsResponses = {
  getTactics: {
    status: APIStatus;
  };
  insertTacticCode: {
    status: APIStatus;
  };
  uploadTactic: {
    status: APIStatus;
    data: Tactic | null;
    message: string | null;
  };
  editTactic: {
    status: APIStatus;
    data: Tactic | null;
    message: string | null;
  };
  deleteTactic: {
    status: APIStatus;
    message: string | null;
  };
};
export interface TacticSortOption {
  label: "Date Added" | "Popularity Score" | "Recommended";
  value: TacticSortOptionValues;
}

export type TacticSortOptionValues =
  | "recommended"
  | "date-added"
  | "popularity-score";

export type Author = {
  name: string;
  logoUrl: string;
};

export type FilterFormat = {
  label: string;
  value: string;
};

type SelectedFilterFormat = {
  label: string;
  value: string;
  category: string;
};

type TacticsState = {
  chosenTactic?: Tactic;
  tactics: Tactic[];
  authors: Author[];
  isFavoriteFilterOn: boolean;
  selectedFilters: {
    positions: SelectedFilterFormat[];
    formation: SelectedFilterFormat[];
    formationGroup: SelectedFilterFormat[];
    author: SelectedFilterFormat[];
  };
  responses: TacticsResponses;
  sort: TacticSortOption;
  totalPages: number;
};

const initialState: TacticsState = {
  tactics: [],
  authors: [],
  isFavoriteFilterOn: false,
  selectedFilters: {
    positions: [],
    formation: [],
    formationGroup: [],
    author: [],
  },
  sort: {
    label: "Recommended",
    value: "recommended",
  },
  responses: {
    getTactics: {
      status: "idle",
    },
    insertTacticCode: {
      status: "idle",
    },
    uploadTactic: {
      status: "idle",
      data: null,
      message: null,
    },
    editTactic: {
      status: "idle",
      data: null,
      message: null,
    },
    deleteTactic: {
      status: "idle",
      message: null,
    },
  },
  totalPages: 1,
};

const transformSelectedFilters = (selectedFilters: SelectedFilterFormat[]) => {
  return selectedFilters.map((filter) => filter.value).join(",");
};

interface FetchTacticsParams {
  page: number;
  formationsAsList: FormationsAsList;
  tacticLimit: number;
}

export const fetchTactics = createAsyncThunk(
  "get-tactics",
  async (
    { page, formationsAsList, tacticLimit }: FetchTacticsParams,
    { getState },
  ) => {
    const state = getState() as IRootState;

    let authorQuery: string | undefined = undefined;
    let positionQuery: string | undefined = undefined;
    let formationQuery: string | undefined = undefined;

    if (state.tacticsReducer.selectedFilters.author.length > 0) {
      authorQuery = transformSelectedFilters(
        state.tacticsReducer.selectedFilters.author,
      );
    }
    if (state.tacticsReducer.selectedFilters.positions.length > 0) {
      positionQuery = transformSelectedFilters(
        state.tacticsReducer.selectedFilters.positions,
      );
    }
    if (state.tacticsReducer.selectedFilters.formationGroup.length > 0) {
      formationQuery = "";
      state.tacticsReducer.selectedFilters.formationGroup.forEach((fg) => {
        const matchingFormations = formationsAsList.filter(
          (formation) =>
            (formation.id.startsWith("f3") &&
              fg.value === FORMATION_GROUPS_3_AT_THE_BACK) ||
            (formation.id.startsWith("f4") &&
              fg.value === FORMATION_GROUPS_4_AT_THE_BACK) ||
            (formation.id.startsWith("f5") &&
              fg.value === FORMATION_GROUPS_5_AT_THE_BACK),
        );
        if (formationQuery.length > 0) {
          formationQuery += ",";
        }
        formationQuery += matchingFormations
          .map((formation) => formation.id)
          .join(",");
      });
    }
    if (state.tacticsReducer.selectedFilters.formation.length > 0) {
      formationQuery =
        (formationQuery ? formationQuery + "," : "") +
        transformSelectedFilters(
          state.tacticsReducer.selectedFilters.formation,
        );
    }
    return apiFc24.getTactics({
      tacticSorting: state.tacticsReducer.sort.value,
      page,
      author: authorQuery,
      position: positionQuery,
      formation: formationQuery,
      tacticLimit,
      ...(state.tacticsReducer.isFavoriteFilterOn && {
        isFavorite: true,
      }),
    });
  },
);

export const fetchAuthors = createAsyncThunk("fetch-authors", async () => {
  return getAuthors();
});

export const trackTacticUsage = createAsyncThunk(
  "track-tactic-usage",
  async (tacticId: string) => {
    return apiFc24.trackTacticUsage(tacticId);
  },
);

export const uploadTactic = createAsyncThunk(
  "upload-tactic",
  async (tactic: { name: string; description: string; eaCode: string }) => {
    const response = await _uploadTactic(tactic);
    return response;
  },
);

export const editTactic = createAsyncThunk(
  "edit-tactic",
  async (tactic: {
    id: string;
    name?: string;
    description?: string;
    eaCode?: string;
    isFavorite?: boolean;
  }) => {
    const response = await _editTactic({
      tacticId: tactic.id,
      description: tactic.description,
      name: tactic.name,
      eaCode: tactic.eaCode,
      isFavorite: tactic.isFavorite,
    });
    return response;
  },
);

export const deleteTactic = createAsyncThunk(
  "delete-tactic",
  async (tacticId: string) => {
    await _deleteTactic(tacticId);
    return tacticId;
  },
);

export const getTacticFromCode = createAsyncThunk<
  Tactic,
  {
    code: string;
  }
>("tactic-pageget-tactic-from-code", async ({ code }) =>
  _getTacticFromCode({ code }),
);

const tacticsSlice = createSlice({
  name: "tactics",
  initialState,
  reducers: {
    updateTacticInsertStatus(state, action: { payload: APIStatus }) {
      state.responses.insertTacticCode.status = action.payload;
    },
    setChosenTactic(state, action: { payload: Tactic }) {
      state.chosenTactic = action.payload;
    },
    setUploadTacticStatusIdle(state) {
      state.responses.uploadTactic = {
        status: "idle",
        data: null,
        message: null,
      };
    },
    setEditTacticStatusIdle(state) {
      state.responses.editTactic = {
        status: "idle",
        data: null,
        message: null,
      };
    },
    setDeleteTacticStatusIdle(state) {
      state.responses.deleteTactic = {
        status: "idle",
        message: null,
      };
    },
    setSort(state, action: { payload: TacticSortOption }) {
      state.sort = action.payload;
    },
    toggleFilter(
      state,
      action: {
        payload: SelectedFilterFormat;
      },
    ) {
      const alreadySelected = state.selectedFilters[
        action.payload.category
      ].some((v) => v.value === action.payload.value);

      if (alreadySelected) {
        state.selectedFilters[action.payload.category] = state.selectedFilters[
          action.payload.category
        ].filter((v) => v.value !== action.payload.value);
      } else {
        state.selectedFilters[action.payload.category].push({
          label: action.payload.label,
          value: action.payload.value,
          category: action.payload.category,
        });
      }
    },
    clearFiltersInCategory(state, action: { payload: { category: string } }) {
      state.selectedFilters[action.payload.category] = [];
    },
    toggleFavoriteFilter(state, action: { payload: boolean }) {
      state.isFavoriteFilterOn = action.payload;
    },
    clearAllFilters(state) {
      state.selectedFilters = {
        positions: [],
        formation: [],
        formationGroup: [],
        author: [],
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTactics.fulfilled, (state, action) => {
        state.tactics = action.payload.tactics;
        state.totalPages = action.payload.totalPages;
        state.responses.getTactics.status = "fulfilled";
      })
      .addCase(fetchTactics.pending, (state) => {
        state.responses.getTactics.status = "pending";
      })
      .addCase(fetchTactics.rejected, (state) => {
        state.responses.getTactics.status = "rejected";
      })
      .addCase(
        getTacticFromCode.fulfilled,
        (state, action: { payload: Tactic }) => {
          state.responses.insertTacticCode.status = "fulfilled";
          state.chosenTactic = setCustomTactic(action.payload);
        },
      )
      .addCase(getTacticFromCode.pending, (state) => {
        state.responses.insertTacticCode.status = "pending";
      })
      .addCase(getTacticFromCode.rejected, (state) => {
        state.responses.insertTacticCode.status = "rejected";
      })
      .addCase(uploadTactic.pending, (state) => {
        state.responses.uploadTactic.status = "pending";
      })
      .addCase(uploadTactic.fulfilled, (state, action) => {
        state.responses.uploadTactic.status = "fulfilled";
        state.tactics = [action.payload, ...state.tactics];
      })
      .addCase(uploadTactic.rejected, (state, action) => {
        state.responses.uploadTactic.status = "rejected";
        if (action.payload === 422) {
          state.responses.uploadTactic.message = "Invalid EA code";
        } else {
          state.responses.uploadTactic.message = "Something went wrong";
        }
      })
      .addCase(editTactic.pending, (state) => {
        state.responses.editTactic.status = "pending";
      })
      .addCase(editTactic.fulfilled, (state, action) => {
        if (state.chosenTactic?.id === action.payload.id) {
          state.chosenTactic = action.payload;
        }
        state.responses.editTactic.status = "fulfilled";
        state.tactics = state.tactics.map((tactic) =>
          tactic.id === action.payload.id ? action.payload : tactic,
        );
      })
      .addCase(editTactic.rejected, (state, action) => {
        state.responses.editTactic.status = "rejected";
        if (action.payload === 422) {
          state.responses.editTactic.message = "Invalid EA code";
        } else {
          state.responses.editTactic.message = "Something went wrong";
        }
      })
      .addCase(deleteTactic.fulfilled, (state, action) => {
        state.responses.deleteTactic.status = "fulfilled";
        state.tactics = state.tactics.filter(
          (tactic) => tactic.id !== action.payload,
        );
      })
      .addCase(deleteTactic.pending, (state) => {
        state.responses.deleteTactic.status = "pending";
      })
      .addCase(deleteTactic.rejected, (state) => {
        state.responses.deleteTactic.status = "rejected";
        state.responses.deleteTactic.message = "Something went wrong";
      })
      .addCase(fetchAuthors.fulfilled, (state, action) => {
        state.authors = action.payload;
      });
  },
});
export const {
  updateTacticInsertStatus,
  setChosenTactic,
  setUploadTacticStatusIdle,
  setEditTacticStatusIdle,
  setDeleteTacticStatusIdle,
  setSort,
  toggleFilter,
  clearFiltersInCategory,
  clearAllFilters,
  toggleFavoriteFilter,
} = tacticsSlice.actions;

export const tacticsSelector = (state: IRootState) => state.tacticsReducer;

export const anyFiltersSelectedSelector = (state: IRootState) => {
  return Object.values(state.tacticsReducer.selectedFilters).some(
    (filter) => filter.length > 0,
  );
};

export const selectedFiltersSelector = (state: IRootState) => {
  const filtersAsArray = Object.values(state.tacticsReducer.selectedFilters);
  return filtersAsArray.flat();
};

export default tacticsSlice.reducer;
